/** Must match @telluscompany/design-system Table localStorage key. */
export const USER_TABLE_PREFERENCES_STORAGE_KEY = "usersPreferences";

export type UserTablePreferences = Record<string, unknown>;

const normalizeOrderColumnKeys = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      if (item && typeof item === "object" && "key" in item) {
        const key = (item as { key: unknown }).key;
        return typeof key === "string" ? key : null;
      }

      return null;
    })
    .filter((key): key is string => Boolean(key));
};

const normalizeUnselectColumnKeys = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is string =>
        typeof item === "string" && item.trim().length > 0,
    )
    .sort();
};

/**
 * Compares only what the user can change in the Table: column order (keys) and
 * hidden columns. Ignores label/minWidth/sortType drift from i18n re-renders.
 */
export const canonicalizeUserTablePreferences = (
  prefs: UserTablePreferences | null | undefined,
): UserTablePreferences => {
  const tablesInput = prefs?.tables;
  if (!tablesInput || typeof tablesInput !== "object") {
    return { tables: {} };
  }

  const canonicalTables: Record<string, unknown> = {};

  for (const [userId, userTables] of Object.entries(tablesInput)) {
    if (!userId || !userTables || typeof userTables !== "object") {
      continue;
    }

    const tableColumns = (userTables as Record<string, unknown>).tableColumns;
    if (!tableColumns || typeof tableColumns !== "object") {
      continue;
    }

    const innerTables = (tableColumns as Record<string, unknown>).tables;
    const canonicalInnerTables: Record<string, unknown> = {};

    if (innerTables && typeof innerTables === "object") {
      for (const [tableId, tablePref] of Object.entries(innerTables)) {
        if (!tableId || !tablePref || typeof tablePref !== "object") {
          continue;
        }

        const pref = tablePref as Record<string, unknown>;
        canonicalInnerTables[tableId] = {
          orderColumns: normalizeOrderColumnKeys(pref.orderColumns),
          unselectColumns: normalizeUnselectColumnKeys(pref.unselectColumns),
        };
      }
    }

    canonicalTables[userId] = {
      tableColumns: { tables: canonicalInnerTables },
    };
  }

  return { tables: canonicalTables };
};

const deepEqualJsonValue = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) {
    return true;
  }

  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") {
    return false;
  }

  if (Array.isArray(a)) {
    return (
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => deepEqualJsonValue(item, b[index]))
    );
  }

  if (Array.isArray(b)) {
    return false;
  }

  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord).sort();
  const bKeys = Object.keys(bRecord).sort();

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  return aKeys.every(
    (key, index) =>
      key === bKeys[index] &&
      deepEqualJsonValue(aRecord[key], bRecord[key]),
  );
};

export const areUserPreferencesEqual = (
  a: UserTablePreferences | null | undefined,
  b: UserTablePreferences | null | undefined,
): boolean =>
  deepEqualJsonValue(
    canonicalizeUserTablePreferences(a),
    canonicalizeUserTablePreferences(b),
  );

/** Overwrites design-system localStorage with server prefs, including empty state. */
export const syncTableUserPreferencesFromServer = (
  serverPreferences: UserTablePreferences | null | undefined,
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      USER_TABLE_PREFERENCES_STORAGE_KEY,
      JSON.stringify(serverPreferences ?? {}),
    );
  } catch {
    // Quota or privacy mode — Table still reads usersPreferencesFromServer.
  }
};

export const shouldPersistTableUserPreferences = (
  next: UserTablePreferences,
  serverPreferences: UserTablePreferences | null | undefined,
): boolean => !areUserPreferencesEqual(next, serverPreferences);
